import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, Component } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "./context/AuthContext";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch() {}
  render() {
    if (this.state.hasError) return (
      <div style={{ minHeight:"100vh", background:"#060609", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, color:"#f1f5f9" }}>
        <div style={{ fontSize:40 }}>⚠️</div>
        <div style={{ fontSize:18, fontWeight:700 }}>Something went wrong</div>
        <div style={{ fontSize:13, color:"#64748b" }}>Please refresh the page to continue.</div>
        <button onClick={() => window.location.reload()} style={{ background:"#f97316", color:"#fff", border:"none", borderRadius:10, padding:"10px 24px", fontSize:14, fontWeight:700, cursor:"pointer" }}>Refresh</button>
      </div>
    );
    return this.props.children;
  }
}
import Navbar         from "./components/Navbar";
import Landing        from "./pages/Landing";
import Search         from "./pages/Search";
import TeacherProfile from "./pages/TeacherProfile";
import Login          from "./pages/Login";
import Register       from "./pages/Register";
import AdminLogin     from "./pages/AdminLogin";
import AdminRegister  from "./pages/AdminRegister";
import Dashboard      from "./pages/Dashboard";
import AdminPanel        from "./pages/AdminPanel";
import AdminTeacherView from "./pages/AdminTeacherView";
import Legal          from "./pages/Legal";
import MyTeachers     from "./pages/MyTeachers";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword  from "./pages/ResetPassword";
import VerifyEmail    from "./pages/VerifyEmail";

function Guard({ children, role }) {
  const { user } = useAuth();
  if (!user) return <Navigate to={role === "admin" ? "/admin-login" : "/login"} replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

const WA_NUMBER = "919569923598";
const WA_MSG    = encodeURIComponent("Hi! I found PadhaiPass and I'd like to know more.");

function WhatsAppFAB() {
  const { pathname } = useLocation();
  const [tapped, setTapped] = useState(false);

  if (pathname.startsWith("/admin")) return null;

  const handleClick = (e) => {
    // On touch devices: first tap shows tooltip, second tap opens WhatsApp
    const isTouch = window.matchMedia("(hover: none)").matches;
    if (isTouch && !tapped) {
      e.preventDefault();
      setTapped(true);
      // Auto-hide tooltip after 3s if no second tap
      setTimeout(() => setTapped(false), 3000);
    }
    // second tap: do nothing special — href opens WhatsApp normally
  };

  return (
    <>
      <style>{`
        @keyframes wa-ping {
          0%   { box-shadow: 0 0 0 0 #25D36677, 0 4px 16px #25D36644; }
          70%  { box-shadow: 0 0 0 12px #25D36600, 0 4px 16px #25D36644; }
          100% { box-shadow: 0 0 0 0 #25D36600,  0 4px 16px #25D36644; }
        }
        .wa-fab {
          width: 52px;
          height: 52px;
          background: #25D366;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          box-shadow: 0 4px 16px #25D36644;
          animation: wa-ping 2.2s ease-out infinite;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          cursor: pointer;
        }
        .wa-fab:hover {
          transform: scale(1.1);
          animation: none;
          box-shadow: 0 6px 24px #25D36677;
        }
        /* Tooltip — shown on hover (desktop) or via .wa-tapped (mobile) */
        .wa-fab-wrap {
          position: fixed;
          bottom: 28px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .wa-fab-label {
          color: #ffffff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.4px;
          white-space: nowrap;
          font-family: inherit;
          text-shadow: 0 1px 4px rgba(0,0,0,0.6);
        }
        @media (max-width: 480px) {
          .wa-fab-wrap { bottom: 20px; right: 16px; }
          .wa-fab { width: 48px; height: 48px; }
          .wa-fab-label { font-size: 10px; }
        }
      `}</style>

      <div className="wa-fab-wrap">
        <a
          href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`}
          target="_blank"
          rel="noreferrer"
          className={`wa-fab${tapped ? " wa-tapped" : ""}`}
          aria-label="Chat with us on WhatsApp"
          onClick={handleClick}
          style={{ position:"static", bottom:"auto", right:"auto" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
        <span className="wa-fab-label">Support</span>
      </div>
    </>
  );
}

function Layout() {
  const { pathname } = useLocation();

  // Show navbar on these pages only
 const showNavbar = [
  "/",
  "/search",
  "/dashboard",
  "/admin",
  "/my-teachers",
  "/about",
  "/privacy",
  "/terms",
  "/refund",
  "/contact",
].includes(pathname) || pathname.startsWith("/teacher/") || pathname.startsWith("/admin/");

  return (
    <>
      {showNavbar && <Navbar />}
      <Routes>
        <Route path="/"               element={<Landing />} />
        <Route path="/search"         element={<Search />} />
        <Route path="/teacher/:id"    element={<TeacherProfile />} />
        <Route path="/login"          element={<Login />} />
        <Route path="/register"       element={<Register />} />
        <Route path="/admin-login"    element={<AdminLogin />} />
        <Route path="/admin-register"    element={<AdminRegister />} />
        <Route path="/forgot-password"       element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token"   element={<VerifyEmail />} />
        <Route path="/dashboard"      element={<Guard role="teacher"><Dashboard /></Guard>} />
        <Route path="/admin"          element={<Guard role="admin"><AdminPanel /></Guard>} />
        <Route path="/admin/teacher/:id" element={<Guard role="admin"><AdminTeacherView /></Guard>} />
        <Route path="/my-teachers"    element={<Guard role="student"><MyTeachers /></Guard>} />
        <Route path="/about"          element={<Legal tab="about"   />} />
        <Route path="/privacy"        element={<Legal tab="privacy" />} />
        <Route path="/terms"          element={<Legal tab="terms"   />} />
        <Route path="/refund"         element={<Legal tab="refund"  />} />
        <Route path="/contact"        element={<Legal tab="contact" />} />
        <Route path="*"               element={<Navigate to="/" replace />} />
      </Routes>
      <WhatsAppFAB />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ErrorBoundary>
          <Layout />
        </ErrorBoundary>
      </BrowserRouter>
    </HelmetProvider>
  );
}